import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import { 
  trialRegistrationService, 
  TrialRegistrationData,
  TrialRegistrationResponse 
} from '../services/trial-registration.service';

interface TrialRegistrationFormProps {
  onSuccess?: (response: TrialRegistrationResponse) => void;
  onError?: (error: string) => void;
}

export const TrialRegistrationForm: React.FC<TrialRegistrationFormProps> = ({
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState<TrialRegistrationData>({
    manufacturerName: '',
    contactEmail: '',
    contactPhone: '',
    password: '',
    businessType: undefined,
    expectedVolume: undefined
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState<{
    checking: boolean;
    available: boolean | null;
    message: string;
  }>({
    checking: false,
    available: null,
    message: ''
  });

  const businessTypeOptions = trialRegistrationService.getBusinessTypeOptions();

  // Debounced email availability check
  useEffect(() => {
    const checkEmail = async () => {
      if (!formData.contactEmail || !trialRegistrationService.validateEmail(formData.contactEmail)) {
        setEmailCheckStatus({ checking: false, available: null, message: '' });
        return;
      }

      setEmailCheckStatus({ checking: true, available: null, message: 'Checking...' });

      try {
        const result = await trialRegistrationService.checkEmailAvailability(formData.contactEmail);
        setEmailCheckStatus({
          checking: false,
          available: result.available,
          message: result.message
        });
      } catch (error) {
        setEmailCheckStatus({
          checking: false,
          available: null,
          message: 'Unable to check email availability'
        });
      }
    };

    const timeoutId = setTimeout(checkEmail, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.contactEmail]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Manufacturer name validation
    if (!trialRegistrationService.validateManufacturerName(formData.manufacturerName)) {
      newErrors.manufacturerName = 'Manufacturer name must be between 2 and 100 characters';
    }

    // Email validation
    if (!formData.contactEmail) {
      newErrors.contactEmail = 'Email address is required';
    } else if (!trialRegistrationService.validateEmail(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    } else if (emailCheckStatus.available === false) {
      newErrors.contactEmail = 'This email address is already registered';
    }

    // Phone validation (optional)
    if (formData.contactPhone && !trialRegistrationService.validatePhone(formData.contactPhone)) {
      newErrors.contactPhone = 'Please enter a valid phone number';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Expected volume validation (optional)
    if (formData.expectedVolume !== undefined && (formData.expectedVolume < 1 || formData.expectedVolume > 1000000)) {
      newErrors.expectedVolume = 'Expected volume must be between 1 and 1,000,000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await trialRegistrationService.registerForTrial(formData);

      if (response.success) {
        onSuccess?.(response);
      } else {
        // Handle validation errors from server
        if (response.errors) {
          const serverErrors: Record<string, string> = {};
          response.errors.forEach(error => {
            serverErrors[error.field] = error.message;
          });
          setErrors(serverErrors);
        } else {
          onError?.(response.message || 'Registration failed');
        }
      }
    } catch (error: any) {
      onError?.(error.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof TrialRegistrationData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
        Start Your Free Trial
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Manufacturer Name */}
        <div>
          <label htmlFor="manufacturerName" className="block text-sm font-medium text-gray-700 mb-1">
            Manufacturer Name *
          </label>
          <Input
            id="manufacturerName"
            type="text"
            value={formData.manufacturerName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('manufacturerName', e.target.value)}
            placeholder="Enter your company name"
            className={errors.manufacturerName ? 'border-red-500' : ''}
            required
          />
          {errors.manufacturerName && (
            <p className="text-red-500 text-sm mt-1">{errors.manufacturerName}</p>
          )}
        </div>

        {/* Contact Email */}
        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
            Contact Email *
          </label>
          <Input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            placeholder="Enter your email address"
            className={errors.contactEmail ? 'border-red-500' : ''}
            required
          />
          {emailCheckStatus.message && (
            <p className={`text-sm mt-1 ${
              emailCheckStatus.checking ? 'text-gray-500' :
              emailCheckStatus.available === true ? 'text-green-600' :
              emailCheckStatus.available === false ? 'text-red-500' : 'text-gray-500'
            }`}>
              {emailCheckStatus.message}
            </p>
          )}
          {errors.contactEmail && (
            <p className="text-red-500 text-sm mt-1">{errors.contactEmail}</p>
          )}
        </div>

        {/* Contact Phone */}
        <div>
          <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-1">
            Contact Phone
          </label>
          <Input
            id="contactPhone"
            type="tel"
            value={formData.contactPhone || ''}
            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
            placeholder="Enter your phone number"
            className={errors.contactPhone ? 'border-red-500' : ''}
          />
          {errors.contactPhone && (
            <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password *
          </label>
          <Input
            id="password"
            type="password"
            value={formData.password || ''}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Enter your password (min 8 characters)"
            className={errors.password ? 'border-red-500' : ''}
            required
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{errors.password}</p>
          )}
        </div>

        {/* Business Type */}
        <div>
          <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
            Business Type
          </label>
          <select
            id="businessType"
            value={formData.businessType || ''}
            onChange={(e) => handleInputChange('businessType', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
          >
            <option value="">Select business type</option>
            {businessTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Expected Volume */}
        <div>
          <label htmlFor="expectedVolume" className="block text-sm font-medium text-gray-700 mb-1">
            Expected Monthly Volume
          </label>
          <Input
            id="expectedVolume"
            type="number"
            min="1"
            max="1000000"
            value={formData.expectedVolume || ''}
            onChange={(e) => handleInputChange('expectedVolume', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Enter expected monthly volume"
            className={errors.expectedVolume ? 'border-red-500' : ''}
          />
          {errors.expectedVolume && (
            <p className="text-red-500 text-sm mt-1">{errors.expectedVolume}</p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || emailCheckStatus.checking || emailCheckStatus.available === false}
          className="w-full"
        >
          {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
        </Button>
      </form>

      <div className="mt-4 text-sm text-gray-600 text-center">
        <p>
          By submitting this form, you agree to our terms of service and privacy policy.
          You will be notified once your trial is activated.
        </p>
      </div>
    </div>
  );
};